// ============================================================
// Lógica pura del formulario público del trabajador (pantallas,
// progreso guardado localmente, y construcción del cuerpo de envío).
// Sin red, sin DOM, sin Date.now() — la hora actual se recibe siempre
// por parámetro, y el almacenamiento se recibe inyectado (StorageLike),
// para poder probarse sin depender de un navegador real.
//
// CITA REAL (fuente): el contrato es el de la Edge Function
// psicosocial-submit — GET ?id=<uuid> devuelve el aviso al trabajador
// (psicosocial-envio.ts, PublicNotice); POST recibe { code, answers }
// con las claves de answers como texto '1'..'58' y valores enteros de
// 1 a 4 (psicosocial-envio.ts, parseSubmitRequest).
//
// DECISIÓN PROPIA (implementación, no de la fuente — pendiente de
// validar con Kevin):
//   - Once pantallas (una por dimensión, la más larga partida en
//     bloques), sin mostrar nombres de dimensión al trabajador, para no
//     condicionar sus respuestas con la categoría psicosocial que se
//     está midiendo en cada pantalla.
//   - Máximo 6 ítems por pantalla (MAX_ITEMS_PER_SCREEN).
//   - El avance se guarda localmente por 48 horas (PROGRESS_TTL_HOURS)
//     y se descarta después, o si no coincide con la forma esperada.
//   - El código de acceso nunca se guarda: vive solo en memoria y en el
//     hash de la URL (#c=...), para que no quede en el historial del
//     navegador ni en el almacenamiento del dispositivo.
//   - El nombre del parámetro del hash es 'c' (corto, para códigos
//     dictados por teléfono); deliberadamente distinto de 'code' para
//     poder detectar enlaces viejos o mal formados.
//   - El avance se guarda por evaluación y NO por persona (el código
//     nunca se guarda, así que no puede servir de clave). Por eso, en
//     un dispositivo compartido, la pantalla que use esto debe
//     preguntar "continuar o empezar de nuevo" antes de mostrar
//     respuestas guardadas, ofrecer un botón para borrar el avance, y
//     borrarlo al enviar con éxito.
// ============================================================

import { isUuid, PublicNotice } from './psicosocial-envio';
import { normalizeCode, formatCode } from './psicosocial-codigos';
import { PSICOSOCIAL_DIMENSIONS } from './psicosocial-instrumento';
import { validateAnswers, Answers } from './psicosocial-calculo';

export const MAX_ITEMS_PER_SCREEN = 6;
export const PROGRESS_TTL_HOURS = 48;

export function readAssessmentId(search: string): string | null {
  const params = new URLSearchParams(search);
  const value = params.get('psicosocial');
  return value !== null && isUuid(value) ? value : null;
}

export function readCodeFromHash(hash: string): string | null {
  const stripped = hash.charAt(0) === '#' ? hash.slice(1) : hash;
  const params = new URLSearchParams(stripped);
  const value = params.get('c');
  if (value === null) return null;
  return normalizeCode(value);
}

export function buildParticipationUrl(origin: string, assessmentId: string, code: string): string {
  if (!isUuid(assessmentId)) {
    throw new Error(`buildParticipationUrl: assessmentId inválido: "${assessmentId}"`);
  }
  const normalized = normalizeCode(code);
  if (normalized === null) {
    throw new Error(`buildParticipationUrl: código inválido: "${code}"`);
  }
  return `${origin}/?psicosocial=${assessmentId}#c=${formatCode(normalized)}`;
}

export interface Screen {
  index: number;
  itemNos: number[];
}

// Una pantalla por dimensión, en el orden del instrumento; la dimensión
// con más de MAX_ITEMS_PER_SCREEN ítems se parte en bloques consecutivos
// de MAX_ITEMS_PER_SCREEN. Sin títulos ni nombres de dimensión — ver
// DECISIÓN PROPIA en la cabecera.
export function buildScreens(): Screen[] {
  const screens: Screen[] = [];
  for (let d = 0; d < PSICOSOCIAL_DIMENSIONS.length; d++) {
    const items = PSICOSOCIAL_DIMENSIONS[d].items;
    if (items.length <= MAX_ITEMS_PER_SCREEN) {
      screens.push({ index: screens.length, itemNos: items.slice() });
    } else {
      for (let start = 0; start < items.length; start += MAX_ITEMS_PER_SCREEN) {
        screens.push({ index: screens.length, itemNos: items.slice(start, start + MAX_ITEMS_PER_SCREEN) });
      }
    }
  }
  return screens;
}

export function answeredCount(answers: Answers): number {
  return Object.keys(answers).length;
}

export function screenComplete(screen: Screen, answers: Answers): boolean {
  for (let i = 0; i < screen.itemNos.length; i++) {
    if (answers[screen.itemNos[i]] === undefined) return false;
  }
  return true;
}

export function firstIncompleteScreen(screens: Screen[], answers: Answers): number {
  for (let i = 0; i < screens.length; i++) {
    if (!screenComplete(screens[i], answers)) return i;
  }
  return -1;
}

export type StorageLike = {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
};

export function progressKey(assessmentId: string): string {
  return 'psicosocial-progreso-' + assessmentId;
}

export function saveProgress(
  storage: StorageLike,
  assessmentId: string,
  progress: { answers: Answers; screenIndex: number },
  now: Date
): boolean {
  try {
    const payload = {
      answers: progress.answers,
      screenIndex: progress.screenIndex,
      savedAt: now.toISOString(),
    };
    storage.setItem(progressKey(assessmentId), JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

export function clearProgress(storage: StorageLike, assessmentId: string): void {
  try {
    storage.removeItem(progressKey(assessmentId));
  } catch {
    // Nunca lanza: el llamador no debe tener que manejar un error al limpiar.
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Nunca lanza: cualquier forma inesperada se trata como "sin progreso
// guardado" y borra la clave, para no dejar basura en el almacenamiento.
export function loadProgress(
  storage: StorageLike,
  assessmentId: string,
  now: Date
): { answers: Answers; screenIndex: number } | null {
  let raw: string | null;
  try {
    raw = storage.getItem(progressKey(assessmentId));
  } catch {
    return null;
  }
  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearProgress(storage, assessmentId);
    return null;
  }

  if (!isPlainObject(parsed)) {
    clearProgress(storage, assessmentId);
    return null;
  }

  const rawAnswers = parsed.answers;
  const rawScreenIndex = parsed.screenIndex;
  const rawSavedAt = parsed.savedAt;

  if (!isPlainObject(rawAnswers)) {
    clearProgress(storage, assessmentId);
    return null;
  }

  const answerKeys = Object.keys(rawAnswers);
  const normalizedAnswers: Answers = {};
  for (let i = 0; i < answerKeys.length; i++) {
    const key = answerKeys[i];
    if (!/^[1-9][0-9]*$/.test(key)) {
      clearProgress(storage, assessmentId);
      return null;
    }
    const n = Number(key);
    if (n < 1 || n > 58) {
      clearProgress(storage, assessmentId);
      return null;
    }
    const value = rawAnswers[key];
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > 4) {
      clearProgress(storage, assessmentId);
      return null;
    }
    normalizedAnswers[n] = value;
  }

  const totalScreens = buildScreens().length;
  if (
    typeof rawScreenIndex !== 'number' ||
    !Number.isInteger(rawScreenIndex) ||
    rawScreenIndex < 0 ||
    rawScreenIndex > totalScreens - 1
  ) {
    clearProgress(storage, assessmentId);
    return null;
  }

  if (typeof rawSavedAt !== 'string') {
    clearProgress(storage, assessmentId);
    return null;
  }
  const savedMs = Date.parse(rawSavedAt);
  if (Number.isNaN(savedMs)) {
    clearProgress(storage, assessmentId);
    return null;
  }

  const ageHours = (now.getTime() - savedMs) / 3600000;
  if (ageHours > PROGRESS_TTL_HOURS) {
    clearProgress(storage, assessmentId);
    return null;
  }
  // Un reloj mal puesto no debe dejar datos guardados indefinidamente:
  // un savedAt "en el futuro" por más de 1 hora también se descarta.
  if (ageHours < -1) {
    clearProgress(storage, assessmentId);
    return null;
  }

  return { answers: normalizedAnswers, screenIndex: rawScreenIndex };
}

export function buildSubmitBody(code: string, answers: Answers): { code: string; answers: Record<string, number> } {
  const normalized = normalizeCode(code);
  if (normalized === null) {
    throw new Error(`buildSubmitBody: código inválido: "${code}"`);
  }
  const validation = validateAnswers(answers);
  if (!validation.ok) {
    throw new Error(`buildSubmitBody: respuestas inválidas: ${validation.errors.join('; ')}`);
  }
  const answersOut: Record<string, number> = {};
  for (let i = 1; i <= 58; i++) {
    answersOut[String(i)] = answers[i];
  }
  return { code: normalized, answers: answersOut };
}

export function submitErrorMessage(status: number | null, errorCode: string | null): string {
  if (status === null) {
    return 'No se pudo conectar. Sus respuestas siguen guardadas en este teléfono; intente de nuevo.';
  }
  if (errorCode === 'codigo_invalido') {
    return 'El código no es válido o ya fue usado. Si ya envió sus respuestas antes, no necesita hacerlo de nuevo.';
  }
  if (errorCode === 'evaluacion_no_abierta') {
    return 'Esta evaluación ya no está recibiendo respuestas.';
  }
  if (errorCode === 'demasiadas_solicitudes') {
    return 'Se recibieron demasiadas solicitudes. Espere unos minutos y vuelva a intentarlo.';
  }
  if (errorCode === 'respuestas_invalidas' || errorCode === 'solicitud_invalida') {
    return 'No se pudieron enviar las respuestas. Revise que haya contestado todas las preguntas.';
  }
  if (errorCode === 'solicitud_demasiado_grande') {
    return 'No se pudieron enviar las respuestas. Intente de nuevo.';
  }
  return 'No se pudo enviar. Intente de nuevo en unos minutos.';
}

export function noticeAvailability(notice: PublicNotice, now: Date): 'disponible' | 'no_abierta' | 'vencida' {
  if (notice.status !== 'abierta') {
    return 'no_abierta';
  }
  if (notice.due_at !== null) {
    const dueMs = Date.parse(notice.due_at);
    if (Number.isNaN(dueMs)) {
      throw new Error(`noticeAvailability: due_at inválido: "${notice.due_at}"`);
    }
    if (dueMs <= now.getTime()) {
      return 'vencida';
    }
  }
  return 'disponible';
}
